import React from "react";
import { Alert, Modal, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { CommercialInvoiceDeepCheck } from "./types";
import { formatCiDisplayValue } from "./formatCiDisplayValue";

const { Text, Title } = Typography;

const COMPARISON_STATUS: Record<
  NonNullable<CommercialInvoiceDeepCheck["bookingComparisons"]>[number]["status"],
  { label: string; color: string }
> = {
  match: { label: "Match", color: "success" },
  mismatch: { label: "Mismatch", color: "error" },
  missing_booking: { label: "Not on booking", color: "default" },
  missing_invoice: { label: "Not on invoice", color: "warning" },
};

const EXTRACTED_LABELS: Record<string, string> = {
  sellerName: "Seller name",
  sellerAddress: "Seller address",
  buyerName: "Buyer name",
  buyerAddress: "Buyer address",
  buyerVat: "Buyer VAT",
  invoiceNumber: "Invoice number",
  invoiceDate: "Invoice date",
  incoterm: "Incoterm",
  currency: "Currency",
  totalAmount: "Total amount",
  grossWeight: "Gross weight",
  netWeight: "Net weight",
  countryOfOrigin: "Country of origin",
};

export interface CommercialInvoiceDeepCheckModalProps {
  open: boolean;
  onClose: () => void;
  deepCheck: CommercialInvoiceDeepCheck;
}

export const CommercialInvoiceDeepCheckModal: React.FC<
  CommercialInvoiceDeepCheckModalProps
> = ({ open, onClose, deepCheck }) => {
  const comparisonColumns: ColumnsType<
    NonNullable<CommercialInvoiceDeepCheck["bookingComparisons"]>[number]
  > = [
    {
      title: "Result",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => {
        const meta = COMPARISON_STATUS[status];
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    { title: "Field", dataIndex: "field", key: "field", width: 140 },
    {
      title: "Booking",
      dataIndex: "bookingValue",
      key: "bookingValue",
      render: (v: unknown) => formatCiDisplayValue(v) ?? "—",
    },
    {
      title: "Invoice",
      dataIndex: "invoiceValue",
      key: "invoiceValue",
      render: (v: unknown) => formatCiDisplayValue(v) ?? "—",
    },
  ];

  if (!deepCheck.available) {
    return (
      <Modal title="AI deep check" open={open} onCancel={onClose} footer={null}>
        <Alert
          type="warning"
          showIcon
          message="AI deep check unavailable"
          description={deepCheck.error ?? "Unknown error"}
        />
      </Modal>
    );
  }

  const extractedEntries = deepCheck.extracted
    ? Object.entries(deepCheck.extracted)
        .map(([key, value]) => [key, formatCiDisplayValue(value)] as const)
        .filter((entry): entry is readonly [string, string] =>
          Boolean(entry[1])
        )
    : [];

  const isProforma =
    deepCheck.documentType === "proforma_invoice" ||
    (deepCheck.proformaLikelihood ?? 0) >= 50;
  const documentTypeLabel =
    deepCheck.documentType === "commercial_invoice"
      ? "Commercial invoice"
      : deepCheck.documentType === "proforma_invoice"
        ? "Proforma / PI"
        : "Uncertain document type";

  return (
    <Modal
      title="AI deep check"
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(820px, calc(100vw - 32px))"
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Alert
          type={isProforma ? "error" : deepCheck.documentType === "uncertain" ? "warning" : "success"}
          showIcon
          message={
            isProforma
              ? "Not a valid customs commercial invoice"
              : deepCheck.documentType === "uncertain"
                ? "Document type uncertain"
                : "Valid commercial invoice (not proforma)"
          }
          description={
            <div>
              <Text style={{ display: "block" }}>
                AI classification: <Text strong>{documentTypeLabel}</Text>
                {deepCheck.proformaLikelihood != null
                  ? ` · ${Math.round(deepCheck.proformaLikelihood)}% proforma confidence`
                  : null}
              </Text>
              {deepCheck.proformaSignals && deepCheck.proformaSignals.length > 0 ? (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {deepCheck.proformaSignals.map((signal) => (
                    <li key={signal} style={{ fontSize: 12 }}>
                      {signal}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          }
        />

        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Model: {deepCheck.model ?? "OpenAI"}
          </Text>
          {deepCheck.summary ? (
            <Text style={{ display: "block", marginTop: 8 }}>{deepCheck.summary}</Text>
          ) : null}
        </div>

        {extractedEntries.length > 0 ? (
          <div>
            <Title level={5} style={{ marginTop: 0 }}>
              Extracted from invoice
            </Title>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {extractedEntries.map(([key, value]) => (
                <li key={key} style={{ fontSize: 13, marginBottom: 4 }}>
                  <Text strong>{EXTRACTED_LABELS[key] ?? key}: </Text>
                  {value}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {deepCheck.lineItems && deepCheck.lineItems.length > 0 ? (
          <div>
            <Title level={5} style={{ marginTop: 0 }}>
              Line items
            </Title>
            <Table
              size="small"
              pagination={false}
              rowKey={(_, i) => String(i)}
              dataSource={deepCheck.lineItems}
              scroll={{ x: true }}
              columns={[
                {
                  title: "Description",
                  dataIndex: "description",
                  ellipsis: true,
                  render: (v: unknown) => formatCiDisplayValue(v) ?? "—",
                },
                {
                  title: "HS",
                  dataIndex: "hsCode",
                  width: 100,
                  render: (v: unknown) => formatCiDisplayValue(v) ?? "—",
                },
                {
                  title: "Qty",
                  dataIndex: "quantity",
                  width: 70,
                  render: (v: unknown) => formatCiDisplayValue(v) ?? "—",
                },
                {
                  title: "Unit",
                  dataIndex: "unit",
                  width: 60,
                  render: (v: unknown) => formatCiDisplayValue(v) ?? "—",
                },
                {
                  title: "Unit price",
                  dataIndex: "unitPrice",
                  width: 90,
                  render: (v: unknown) => formatCiDisplayValue(v) ?? "—",
                },
                {
                  title: "Total",
                  dataIndex: "total",
                  width: 90,
                  render: (v: unknown) => formatCiDisplayValue(v) ?? "—",
                },
              ]}
            />
          </div>
        ) : null}

        {deepCheck.bookingComparisons && deepCheck.bookingComparisons.length > 0 ? (
          <div>
            <Title level={5} style={{ marginTop: 0 }}>
              Booking cross-check (AI)
            </Title>
            <Table
              size="small"
              pagination={false}
              rowKey={(_, i) => String(i)}
              dataSource={deepCheck.bookingComparisons}
              columns={comparisonColumns}
              scroll={{ x: true }}
            />
          </div>
        ) : null}
      </Space>
    </Modal>
  );
};
